import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent } from "vue"

const _hoisted_1 = { class: "flex flex-column flex-grow-1" }
const _hoisted_2 = { class: "mx-auto mb-s3 sm:mb-s4 xl:mb-s6" }
const _hoisted_3 = { class: "panel-frame" }
const _hoisted_4 = { class: "flex flex-wrap align-items-end justify-content-between gutter-s3" }
const _hoisted_5 = { class: "w-full sm:w-6 xl:w-auto" }
const _hoisted_6 = { class: "h3 mb-s1 text-bold" }
const _hoisted_7 = { class: "sm:ml-auto xl:pb-s2" }
const _hoisted_8 = { class: "flex w-full sm:w-auto sm:flex-order-2 xl:flex-order-1" }
const _hoisted_9 = { class: "flex-grow-1" }
const _hoisted_10 = { class: "xl:w-12 flex-grow-1 flex-order-1" }
const _hoisted_11 = { class: "mt-s3" }

import { computed, markRaw, onMounted, onUnmounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BigNumber from 'bignumber.js';
import TextSwitch from '@/components/switch/TextSwitch.vue';
import TokenListDeposit from '@/views/pages/liquidity/single-side/deposit/TokenListDeposit.vue';
import TokenListWithdraw from '@/views/pages/liquidity/single-side/withdraw/TokenListWithdraw.vue';
import NoFeeLimitFilter from '@/views/pages/liquidity/single-side/components/NoFeeLimitFilter.vue';
import Switch from '@/components/switch/Switch.vue';
import SortingListDropdown from '@/components/SortingListDropdown.vue';
import Settings from '@/views/pages/liquidity/settingsModal/Settings.vue';
import SearchInput from '@/components/form/SearchInput.vue';
import { useStore } from 'vuex';
import ListEmptyMessage from '@/views/pages/liquidity/single-side/components/ListEmptyMessage.vue';
import TokenListPlaceholder from '@/views/pages/liquidity/single-side/components/TokenListPlaceholder.vue';
import { useEVMWallet } from '@/store/modules/wallet/useEVMWallet';
import { useSingleSide } from '@/store/modules/single-side/useSingleSide';
import { useFarmingTokens } from '@/store/modules/tokens/useFarmingTokens';
import { useSingleSideDepositTokens } from './composables/useSingleSideDepositTokens';
import { useSingleSideWithdrawTokens } from './composables/useSingleSideWithdrawTokens';
import { useSingleSideDepositSteps } from '@/store/modules/single-side/useSingleSideDepositSteps';
import { SingleSideToken } from './models/single-side-token';
import { SingleSideActionName } from './models/single-side';
import SingleSideSteps from '@/views/pages/liquidity/single-side/components/SingleSideSteps.vue';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import { useSingleSideWithdrawSteps } from '@/store/modules/single-side/useSingleSideWithdrawSteps';
import { GITBOOK_LINKS } from '@/constants/EXTERNAL_LINKS';
import SectionHeader from '@/components/SectionHeader.vue';

type SingleSideAction = {
  label: string;
  value: SingleSideActionName;
};
type SortModel = {
  [key in SingleSideActionName]: {
    sortBy: string;
    direction: 'asc' | 'desc';
  };
};

enum SORT_FIELD {
  APR = 0,
  NO_FEE_LIMIT = 1,
  TOKEN_NAME = 2,
  PORTFOLIO_NAME = 3,
}

export default /*#__PURE__*/_defineComponent({
  __name: 'SingleSide',
  setup(__props) {

const { t } = useI18n();
const actions: SingleSideAction[] = [
  { label: 'Provide liquidity', value: 'deposit' },
  { label: 'Withdraw', value: 'withdraw' },
];
const action = ref<SingleSideActionName>('deposit');
const isDeposit = computed(() => action.value === 'deposit');
const tokenListComponent = markRaw(
  computed(() => (action.value === 'deposit' ? TokenListDeposit : TokenListWithdraw)),
);
const { state } = useStore();
const { walletState } = useEVMWallet();
const singleSideStore = useSingleSide();
const farmingTokensStore = useFarmingTokens();
const singleSideDepositTokens = useSingleSideDepositTokens();
const singleSideWithdrawTokens = useSingleSideWithdrawTokens();
const {
  singleSideDepositSteps,
  prepareSteps: prepareSideDepositSteps,
  runSteps: runSideDepositSteps,
} = useSingleSideDepositSteps();
const {
  singleSideWithdrawSteps,
  prepareSteps: prepareSideWithdrawSteps,
  runSteps: runSideWithdrawSteps,
} = useSingleSideWithdrawSteps();

const noFeeLimit = ref(0);
const isHideZero = ref(false);
const isProvidedOnly = ref(walletState.isInjected);
const showSteps = ref(false);

const sortFields: { label: string; value: string }[] = [];
sortFields[SORT_FIELD.APR] = { label: 'APR', value: 'apr' };
sortFields[SORT_FIELD.NO_FEE_LIMIT] = { label: 'No fee limit', value: 'noFeeLimit' };
sortFields[SORT_FIELD.TOKEN_NAME] = { label: 'Token name', value: 'token' };
sortFields[SORT_FIELD.PORTFOLIO_NAME] = { label: 'Portfolio', value: 'portfolio' };

const sortFns = {
  // apr
  [sortFields[SORT_FIELD.APR].value]: (prev: SingleSideToken, next: SingleSideToken) =>
    prev.apr.minus(next.apr).toNumber(),
  // no fee limit
  [sortFields[SORT_FIELD.NO_FEE_LIMIT].value]: (prev: SingleSideToken, next: SingleSideToken) =>
    prev.noFeeLimitInUSD.minus(next.noFeeLimitInUSD).toNumber(),
  // token name
  [sortFields[SORT_FIELD.TOKEN_NAME].value]: (prev: SingleSideToken, next: SingleSideToken) =>
    prev.portfolioToken.symbol!.toLowerCase() < next.portfolioToken.symbol!.toLowerCase() ? -1 : 1,
  // portfolio name
  [sortFields[SORT_FIELD.PORTFOLIO_NAME].value]: (prev: SingleSideToken, next: SingleSideToken) =>
    prev.portfolio.name.toLowerCase() < next.portfolio.name.toLowerCase() ? -1 : 1,
};

const sortModels = reactive<SortModel>({
  deposit: {
    sortBy: sortFields[SORT_FIELD.APR].value,
    direction: 'desc',
  },
  withdraw: {
    sortBy: sortFields[SORT_FIELD.APR].value,
    direction: 'asc',
  },
});
const searchFilter = ref('');
const noSearchResult = ref(false);

onMounted(() => {
  farmingTokensStore.startIntervalUpdateFarmingToken();
});

onUnmounted(() => {
  farmingTokensStore.cleanIntervalUpdateFarmingToken();
});

const isTextSwitchDisabled = computed(() => {
  return (
    ENABLE_FAKE_CARDANO_NETWORK &&
    (singleSideDepositSteps.isShown || singleSideWithdrawSteps.isShown)
  );
});

const farmsBySingleSideToken = computed(() =>
  action.value === 'deposit'
    ? singleSideDepositTokens.farmsBySingleSideToken.value
    : singleSideWithdrawTokens.farmsBySingleSideToken.value,
);
const allTokens = computed<SingleSideToken[]>(() =>
  action.value === 'deposit'
    ? singleSideDepositTokens.tokens.value.filter(token =>
        isHideZero.value ? BigNumber(token.balance ?? '0').gt(0) : true,
      )
    : singleSideWithdrawTokens.tokens.value.filter(token =>
        isProvidedOnly.value ? token.balanceLPInWallet.plus(token.balanceLPInFarm).gt(0) : true,
      ),
);
const tokens = computed<SingleSideToken[]>(() =>
  allTokens.value
    .filter(token => token.noFeeLimitInUSD.gte(BigNumber(noFeeLimit.value)))
    .filter(token => {
      return (
        token.portfolio.name.toLowerCase().includes(searchFilter.value.trim().toLowerCase()) ||
        (token.portfolioToken.symbol ?? '')
          .toLowerCase()
          .includes(searchFilter.value.trim().toLowerCase())
      );
    })
    .sort((prev, next) => {
      const direction = sortModels[action.value].direction === 'asc' ? 1 : -1;
      const sortFn = sortFns[sortModels[action.value].sortBy];
      return (sortFn ? sortFn(prev, next) : 1) * direction;
    }),
);
const searchInputPlaceholder = computed(() =>
  state.layout.isMobile ? t('search') : t('singleSide.searchPlaceholder'),
);
const isLoadingTokens = computed(() => !farmingTokensStore.isStoreReady);

const onApplySettings = ({ settings }) => {
  singleSideStore.setTransactionSettings(settings);
};

function prepareSteps() {
  const prepare = action.value === 'deposit' ? prepareSideDepositSteps : prepareSideWithdrawSteps;
  prepare();
}

function runSteps() {
  const run = action.value === 'deposit' ? runSideDepositSteps : runSideWithdrawSteps;
  run();
}

const onShowSteps = () => {
  showSteps.value = true;
  if (action.value === 'deposit') {
    singleSideDepositSteps.isShown = true;
  } else {
    singleSideWithdrawSteps.isShown = true;
  }
};

const onHideSteps = () => {
  showSteps.value = false;
  if (action.value === 'deposit') {
    singleSideDepositSteps.isShown = false;
  } else {
    singleSideWithdrawSteps.isShown = false;
  }
};

const onSendTransaction = () => {
  if (ENABLE_FAKE_CARDANO_NETWORK) {
    prepareSteps();
    onShowSteps();
    runSteps();
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(SectionHeader, {
      section: "singleSide",
      "sub-section": action.value,
      "how-link": _unref(GITBOOK_LINKS).SINGLE_SIDE
    }, null, 8, ["sub-section", "how-link"]),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(TextSwitch, {
        values: actions,
        modelValue: action.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((action).value = $event)),
        disabled: _unref(isTextSwitchDisabled)
      }, null, 8, ["modelValue", "disabled"])
    ]),
    (showSteps.value)
      ? (_openBlock(), _createBlock(SingleSideSteps, {
          key: 0,
          action: action.value,
          class: "mb-s3",
          onOnClose: _cache[1] || (_cache[1] = ($event: any) => (onHideSteps()))
        }, null, 8, ["action"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(t)(`singleSide.${action.value}.listTitle`)), 1),
          _createVNode(NoFeeLimitFilter, {
            modelValue: noFeeLimit.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((noFeeLimit).value = $event))
          }, null, 8, ["modelValue"])
        ]),
        _createElementVNode("div", _hoisted_7, [
          (_unref(walletState).isInjected)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                (_unref(isDeposit))
                  ? (_openBlock(), _createBlock(Switch, {
                      key: 0,
                      name: _unref(t)('singleSide.hideZero'),
                      "name-pos": _unref(state).layout.isMobile ? 'right' : 'left',
                      modelValue: isHideZero.value,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((isHideZero).value = $event)),
                      disabled: !_unref(tokens).length && !isHideZero.value
                    }, null, 8, ["name", "name-pos", "modelValue", "disabled"]))
                  : (_openBlock(), _createBlock(Switch, {
                      key: 1,
                      name: _unref(t)('singleSide.providedOnly'),
                      "name-pos": _unref(state).layout.isMobile ? 'right' : 'left',
                      modelValue: isProvidedOnly.value,
                      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((isProvidedOnly).value = $event)),
                      disabled: !_unref(tokens).length && !isProvidedOnly.value
                    }, null, 8, ["name", "name-pos", "modelValue", "disabled"]))
              ], 64))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_8, [
          _createElementVNode("div", _hoisted_9, [
            (action.value === 'deposit')
              ? (_openBlock(), _createBlock(SortingListDropdown, {
                  key: 0,
                  "sort-fields": sortFields,
                  direction: sortModels.deposit.direction,
                  "onUpdate:direction": _cache[5] || (_cache[5] = ($event: any) => ((sortModels.deposit.direction) = $event)),
                  "sort-by": sortModels.deposit.sortBy,
                  "onUpdate:sort-by": _cache[6] || (_cache[6] = ($event: any) => ((sortModels.deposit.sortBy) = $event)),
                  disabled: !_unref(tokens).length,
                  class: "mr-s2"
                }, null, 8, ["direction", "sort-by", "disabled"]))
              : (_openBlock(), _createBlock(SortingListDropdown, {
                  key: 1,
                  "sort-fields": sortFields,
                  direction: sortModels.withdraw.direction,
                  "onUpdate:direction": _cache[7] || (_cache[7] = ($event: any) => ((sortModels.withdraw.direction) = $event)),
                  "sort-by": sortModels.withdraw.sortBy,
                  "onUpdate:sort-by": _cache[8] || (_cache[8] = ($event: any) => ((sortModels.withdraw.sortBy) = $event)),
                  disabled: !_unref(tokens).length,
                  class: "mr-s2"
                }, null, 8, ["direction", "sort-by", "disabled"]))
          ]),
          _createElementVNode("div", null, [
            _createVNode(Settings, {
              type: "button",
              disabled: false,
              "on-apply-settings": onApplySettings,
              formSettings: _unref(singleSideStore).settings,
              "btn-class": "button-base button-icon text-primary"
            }, null, 8, ["formSettings"])
          ])
        ]),
        _createElementVNode("div", _hoisted_10, [
          _createVNode(SearchInput, {
            modelValue: searchFilter.value,
            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((searchFilter).value = $event)),
            placeholder: _unref(searchInputPlaceholder),
            class: "w-full",
            disabled: !_unref(tokens).length && !searchFilter.value
          }, null, 8, ["modelValue", "placeholder", "disabled"])
        ])
      ]),
      _createElementVNode("div", _hoisted_11, [
        (_unref(isLoadingTokens))
          ? (_openBlock(), _createBlock(TokenListPlaceholder, {
              key: 0,
              action: action.value
            }, null, 8, ["action"]))
          : (_unref(tokens).length)
            ? (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(tokenListComponent)), {
                key: 1,
                noFeeLimit: noFeeLimit.value,
                sortBy: sortModels[action.value].sortBy,
                sortDirection: sortModels[action.value].direction,
                tokens: _unref(tokens),
                farms: _unref(farmsBySingleSideToken),
                onSendTransaction: onSendTransaction
              }, null, 40, ["noFeeLimit", "sortBy", "sortDirection", "tokens", "farms"]))
            : (_openBlock(), _createBlock(ListEmptyMessage, {
                key: 2,
                action: action.value,
                "no-fee-limit": noFeeLimit.value,
                "is-hide-zero": isHideZero.value,
                "is-provided-only": isProvidedOnly.value,
                "search-filter": noSearchResult.value ? searchFilter.value : null
              }, null, 8, ["action", "no-fee-limit", "is-hide-zero", "is-provided-only", "search-filter"]))
      ])
    ])
  ]))
}
}

})